import { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: returning from Google OAuth — AuthCallback exchanges the session_id first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(async () => {
        // Preview environment: sign in automatically so preview links just open (preview hosts only).
        if (window.location.hostname.includes("preview")) {
          try {
            await api.post("/auth/preview-login", { role: "admin" });
            const r = await api.get("/auth/me");
            setUser(r.data);
            return;
          } catch (e) { /* fall through to logged-out */ }
        }
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data && data.mfa_required) return data; // code step — user set only after verification
    setUser(data);
    return data;
  };

  const verifyLoginCode = async (mfaToken, code) => {
    const { data } = await api.post("/auth/login/verify", { mfa_token: mfaToken, code });
    setUser(data);
    return data;
  };

  const resendLoginCode = async (mfaToken) => {
    const { data } = await api.post("/auth/login/resend", { mfa_token: mfaToken });
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyLoginCode, resendLoginCode, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
